/* ============================================================
   LICK THEME — theme.js
   ============================================================ */

(function () {
  'use strict';

  // ── HEADER SCROLL BEHAVIOUR ────────────────────────────────
  // Looked up on each scroll: the theme editor can replace the header (§8du)
  const handleScroll = () => {
    document.getElementById('SiteHeader')?.classList.toggle('is-scrolled', window.scrollY > 10);
  };
  window.addEventListener('scroll', handleScroll, { passive: true });
  handleScroll();

  // ── MOBILE MENU ────────────────────────────────────────────
  // Looked up when used and clicks delegated from the document (§8du): the theme editor
  // re-renders the header section, which would orphan listeners bound to its elements
  const byId = (id) => document.getElementById(id);

  // inert while closed, so Tab can't reach the off-screen menu; focus moves in on open
  // and back to the menu button on close
  function openMobileMenu() {
    const mobileMenu = byId('MobileMenu');
    if (!mobileMenu) return;
    mobileMenu.classList.add('is-open');
    byId('MobileMenuOverlay')?.classList.add('is-visible');
    byId('MobileMenuToggle')?.setAttribute('aria-expanded', 'true');
    mobileMenu.setAttribute('aria-hidden', 'false');
    mobileMenu.inert = false;
    document.body.style.overflow = 'hidden';
    byId('MobileMenuClose')?.focus({ preventScroll: true });
  }

  function closeMobileMenu() {
    const mobileMenu = byId('MobileMenu');
    if (!mobileMenu) return;
    const hadFocus = mobileMenu.contains(document.activeElement);
    mobileMenu.classList.remove('is-open');
    byId('MobileMenuOverlay')?.classList.remove('is-visible');
    byId('MobileMenuToggle')?.setAttribute('aria-expanded', 'false');
    mobileMenu.setAttribute('aria-hidden', 'true');
    mobileMenu.inert = true;
    document.body.style.overflow = '';
    if (hadFocus) byId('MobileMenuToggle')?.focus({ preventScroll: true });
  }

  document.addEventListener('click', (e) => {
    if (e.target.closest('#MobileMenuToggle')) openMobileMenu();
    else if (e.target.closest('#MobileMenuClose, #MobileMenuOverlay')) closeMobileMenu();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeMobileMenu();
      closeHeaderSearch();
    }
  });

  // ── HEADER SEARCH DRAWER ────────────────────────────────────
  // Found by class: the desktop bar and the mobile bar each have a toggle (§8cx). Looked
  // up when used, clicks delegated (§8du), as the editor re-renders the header.
  const searchToggles = () => document.querySelectorAll('.site-header__search-toggle');

  function openHeaderSearch() {
    const searchDrawer = byId('HeaderSearch');
    if (!searchDrawer) return;
    // Open just below the header's bottom edge, wherever its height setting or
    // sticky puts it — not over the menu row (§8cx). 0 once scrolled away.
    const siteHeader = byId('SiteHeader');
    const headerBottom = siteHeader ? Math.max(0, siteHeader.getBoundingClientRect().bottom) : 0;
    searchDrawer.style.setProperty('--search-top', `${Math.round(headerBottom)}px`);
    searchDrawer.classList.add('is-open');
    byId('HeaderSearchBackdrop')?.classList.add('is-visible');
    searchToggles().forEach((t) => t.setAttribute('aria-expanded', 'true'));
    searchDrawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    window.setTimeout(() => {
      byId('HeaderSearchInput')?.focus();
    }, 150);
  }

  function closeHeaderSearch() {
    const searchDrawer = byId('HeaderSearch');
    if (!searchDrawer) return;
    searchDrawer.classList.remove('is-open');
    byId('HeaderSearchBackdrop')?.classList.remove('is-visible');
    searchToggles().forEach((t) => t.setAttribute('aria-expanded', 'false'));
    searchDrawer.setAttribute('aria-hidden', 'true');
    if (!byId('MobileMenu')?.classList.contains('is-open')) {
      document.body.style.overflow = '';
    }
  }

  document.addEventListener('click', (e) => {
    if (e.target.closest('.site-header__search-toggle')) {
      if (byId('HeaderSearch')?.classList.contains('is-open')) closeHeaderSearch();
      else openHeaderSearch();
    } else if (e.target.closest('#HeaderSearchClose, #HeaderSearchBackdrop')) {
      closeHeaderSearch();
    }
  });

  // ── ROOM CAROUSEL ──────────────────────────────────────────
  const track    = document.getElementById('RoomTrack');
  const prevBtn  = document.getElementById('RoomPrev');
  const nextBtn  = document.getElementById('RoomNext');

  if (track && prevBtn && nextBtn) {
    const getScrollAmount = () => {
      const card = track.querySelector('.room-card');
      return card ? card.offsetWidth + 16 : 300;
    };

    const updateButtons = () => {
      prevBtn.disabled = track.parentElement.scrollLeft <= 0;
      nextBtn.disabled =
        track.parentElement.scrollLeft + track.parentElement.offsetWidth >=
        track.scrollWidth - 1;
    };

    prevBtn.addEventListener('click', () => {
      track.parentElement.scrollBy({ left: -getScrollAmount() * 3, behavior: 'smooth' });
    });

    nextBtn.addEventListener('click', () => {
      track.parentElement.scrollBy({ left: getScrollAmount() * 3, behavior: 'smooth' });
    });

    track.parentElement.addEventListener('scroll', updateButtons, { passive: true });
    updateButtons();

    // Drag-to-scroll
    let isDown = false, startX, scrollStart;

    track.parentElement.addEventListener('mousedown', (e) => {
      isDown = true;
      startX = e.pageX - track.parentElement.offsetLeft;
      scrollStart = track.parentElement.scrollLeft;
    });

    track.parentElement.addEventListener('mouseleave', () => { isDown = false; });
    track.parentElement.addEventListener('mouseup', () => { isDown = false; });

    track.parentElement.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - track.parentElement.offsetLeft;
      const walk = (x - startX) * 1.5;
      track.parentElement.scrollLeft = scrollStart - walk;
    });
  }

  // ── ADD TO CART (AJAX) ─────────────────────────────────────
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('.product-card__form, [data-add-to-cart]');
    if (!form) return;
    e.preventDefault();

    const btn = form.querySelector('button[type="submit"]');
    const iconOnly = btn.hasAttribute('data-icon');
    const originalText = btn.textContent;

    if (!iconOnly) btn.textContent = 'Adding…';
    btn.disabled = true;

    try {
      const formData = new FormData(form);
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      const data = await response.json();
      if (iconOnly) btn.classList.add('is-added');
      else btn.textContent = 'Added';

      // Update the header cart count bubbles
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      document.querySelectorAll('.cart-count').forEach((el) => {
        el.textContent = cartData.item_count;
      });

      setTimeout(() => {
        if (iconOnly) btn.classList.remove('is-added');
        else btn.textContent = originalText;
        btn.disabled = false;
        qvKeepSoldOut(form, btn);
      }, 2000);

    } catch (err) {
      console.error('Cart error:', err);
      if (!iconOnly) btn.textContent = 'Try again';
      setTimeout(() => {
        if (!iconOnly) btn.textContent = originalText;
        btn.disabled = false;
        qvKeepSoldOut(form, btn);
      }, 2000);
    }
  });

  // ── CART ATTRIBUTION ───────────────────────────────────────
  // The outgoing React storefront used last-touch sessionStorage. This theme
  // intentionally uses first-touch localStorage so attribution survives the
  // shopper's return visits for 30 days.
  const attributionStoragePrefix = 'lick_';
  const attributionExpiryKey = `${attributionStoragePrefix}attribution_expires_at`;
  const attributionLifetimeMs = 30 * 24 * 60 * 60 * 1000;
  const attributionSearchKeys = [
    ['utm_source', 'lick_utm_source'],
    ['utm_medium', 'lick_utm_medium'],
    ['utm_campaign', 'lick_utm_campaign'],
    ['utm_content', 'lick_utm_content'],
    ['utm_term', 'lick_utm_term'],
    ['gclid', 'lick_click_gclid'],
    ['fbclid', 'lick_click_fbclid'],
    ['msclkid', 'lick_click_msclkid'],
    ['ttclid', 'lick_click_ttclid'],
  ];
  let attributionWriteQueue = Promise.resolve();

  function readCookie(name) {
    try {
      const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')}=([^;]*)`));
      if (!match || !match[1]) return '';
      return decodeURIComponent(match[1]).trim();
    } catch (err) {
      return '';
    }
  }

  function readRawCookie(name) {
    try {
      const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')}=([^;]*)`));
      return match && match[1] ? match[1].trim() : '';
    } catch (err) {
      return '';
    }
  }

  function captureFirstTouchAttribution() {
    try {
      const now = Date.now();
      const expiry = Number(localStorage.getItem(attributionExpiryKey));
      if (expiry && expiry <= now) {
        attributionSearchKeys.forEach(([, storageKey]) => localStorage.removeItem(storageKey));
        localStorage.removeItem(attributionExpiryKey);
      }

      const params = new URLSearchParams(window.location.search);
      let wroteFirstValue = false;
      attributionSearchKeys.forEach(([param, storageKey]) => {
        const value = (params.get(param) || '').trim();
        if (value && !localStorage.getItem(storageKey)) {
          localStorage.setItem(storageKey, value);
          wroteFirstValue = true;
        }
      });

      if (wroteFirstValue && !localStorage.getItem(attributionExpiryKey)) {
        localStorage.setItem(attributionExpiryKey, String(now + attributionLifetimeMs));
      }
    } catch (err) {
      // Storage can be unavailable in private browsing or when blocked.
    }
  }

  function storedAttributionValues() {
    const values = {};

    const segmentClientId = readCookie('rl_anonymous_id');
    if (segmentClientId) values['segment-clientID'] = segmentClientId;

    // Shopify sets _shopify_y on every session independently of CDP or consent,
    // so it survives RudderStack retirement. PostHog's checkout pixel already
    // registers this same value as shopify_client_id.
    const shopifyClientId = readRawCookie('_shopify_y');
    if (shopifyClientId) values['shopify-clientID'] = shopifyClientId;

    // Do not derive a Google ID here. The old React app read window.ga
    // (Universal Analytics, now dead); this store has no first-party _ga cookie.
    // RudderStack's rs_ga cookies are being retired, so only rl_anonymous_id is kept.

    try {
      const expiry = Number(localStorage.getItem(attributionExpiryKey));
      if (!expiry || expiry <= Date.now()) return values;

      attributionSearchKeys.forEach(([param, storageKey]) => {
        const value = (localStorage.getItem(storageKey) || '').trim();
        if (value) values[param] = value;
      });
    } catch (err) {
      // Cookie-derived IDs remain available if localStorage is unavailable.
    }

    return values;
  }

  async function updateCartAttribution() {
    const values = storedAttributionValues();
    if (!Object.keys(values).length) return;

    const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!cartResponse.ok) throw new Error(`Could not read cart attributes: ${cartResponse.status}`);
    const cart = await cartResponse.json();

    // /cart/update.js replaces attributes rather than merging them. Always post
    // the complete existing object plus our values to avoid dropping client IDs.
    const attributes = Object.assign({}, cart.attributes || {}, values);
    const updateResponse = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    });
    if (!updateResponse.ok) throw new Error(`Could not update cart attributes: ${updateResponse.status}`);
  }

  function writeCartAttribution() {
    // Serialising read-merge-write cycles prevents concurrent cart additions
    // from overwriting one another's attributes.
    attributionWriteQueue = attributionWriteQueue
      .catch(() => {})
      .then(updateCartAttribution)
      .catch((err) => {
        console.warn('Cart attribution update failed:', err);
      });
    return attributionWriteQueue;
  }

  function writeCartAttributionWithin(timeoutMs) {
    return new Promise((resolve) => {
      const timeout = window.setTimeout(resolve, timeoutMs);
      writeCartAttribution().finally(() => {
        window.clearTimeout(timeout);
        resolve();
      });
    });
  }

  captureFirstTouchAttribution();
  window.lickCartAttribution = { write: writeCartAttribution };

  // Every current theme add-to-cart path uses fetch. Hooking it once prevents
  // section-local cart handlers from drifting out of attribution coverage.
  const nativeFetch = window.fetch.bind(window);
  let standardEventsPromise;

  function loadStandardEvents() {
    if (window.StandardEvents) return Promise.resolve(window.StandardEvents);
    if (!standardEventsPromise) {
      standardEventsPromise = import('https://cdn.shopify.com/storefront/standard-events.js')
        .then((module) => module)
        .catch((err) => {
          console.warn('Shopify standard events unavailable:', err);
          return null;
        });
    }
    return standardEventsPromise;
  }

  async function dispatchCartAddedEvent(response) {
    try {
      const payload = await response.clone().json();
      const items = Array.isArray(payload.items) ? payload.items : [payload];
      const lines = items
        .filter((item) => item && item.variant_id)
        .map((item) => ({
          merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`,
          quantity: Number(item.quantity) || 1,
        }));
      if (!lines.length) return;

      const mod = await loadStandardEvents();
      if (!mod?.CartLinesUpdateEvent) return;
      const { CartLinesUpdateEvent, CartErrorEvent } = mod;

      const deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines,
        promise: deferred.promise,
      }));

      try {
        const cartResponse = await nativeFetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error(`Could not read cart for standard event: ${cartResponse.status}`);
        const cart = await cartResponse.json();
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
      } catch (err) {
        deferred.reject(err);
        if (CartErrorEvent) {
          document.dispatchEvent(new CartErrorEvent({
            error: err.message,
            code: 'SERVICE_UNAVAILABLE',
          }));
        }
      }
    } catch (err) {
      // Pixel notification must never alter or reject the add-to-cart request.
      console.warn('Shopify cart event dispatch failed:', err);
    }
  }

  // Begin loading once at page load; add-to-cart never waits for the network
  // module before returning the native cart response or writing attribution.
  loadStandardEvents();

  function isCartAddRequest(input) {
    try {
      const isRequest = typeof Request !== 'undefined' && input instanceof Request;
      const url = new URL(isRequest ? input.url : input, window.location.origin);
      return url.pathname === '/cart/add.js';
    } catch (err) {
      return false;
    }
  }

  window.fetch = function lickAttributionFetch(input, init) {
    // Non-cart requests receive the original native promise directly. The
    // response body is never read or cloned: callers keep the exact Response.
    if (!isCartAddRequest(input)) return nativeFetch(input, init);

    return nativeFetch(input, init).then((response) => {
      try {
        if (response.ok) {
          writeCartAttribution();
          dispatchCartAddedEvent(response).catch(() => {
            // Defensive guard: event notification must never affect callers.
          });
        }
      } catch (err) {
        // Attribution and pixel notification must never change the result of
        // the add-to-cart request.
      }
      return response;
    });
  };

  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement) || form.id !== 'CartPageForm') return;
    if (form.dataset.attributionSubmitting === 'true') return;

    e.preventDefault();
    form.dataset.attributionSubmitting = 'true';
    const submitter = e.submitter;

    writeCartAttributionWithin(1000).finally(() => {
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(submitter || form.querySelector('[name="checkout"]'));
        return;
      }

      // requestSubmit preserves the original submitter. This fallback preserves
      // Shopify's checkout POST marker along with every existing form field.
      const checkoutInput = document.createElement('input');
      checkoutInput.type = 'hidden';
      checkoutInput.name = 'checkout';
      checkoutInput.value = '';
      form.appendChild(checkoutInput);
      form.submit();
    });
  });

  document.addEventListener('click', (e) => {
    const checkoutLink = e.target.closest('a[href]');
    if (!checkoutLink) return;

    let url;
    try {
      url = new URL(checkoutLink.href, window.location.origin);
    } catch (err) {
      return;
    }
    if (url.origin !== window.location.origin || url.pathname !== '/checkout') return;

    e.preventDefault();
    writeCartAttributionWithin(1000).finally(() => window.location.assign(url.href));
  });

  // ── QUICK VIEW ─────────────────────────────────────────────
  const qvMoney = (pence) =>
    window.lickMoney ? window.lickMoney(pence) : '£' + (pence / 100).toFixed(2);

  const qvImage = (url, width) =>
    url ? url + (url.includes('?') ? '&' : '?') + 'width=' + width : '';

  // Paint quick view: finish tiles instead of the variant dropdown (§8dt, 2026-10-21,
  // Rhys: default to Ultra Flat Matt with a "Recommended" badge, and show each finish's
  // text). The text is hard-coded for now, to move to metaobjects later; keyed by the
  // Finish option value, with "Sample" for the sample. (Matt and Eggshell share the text
  // as supplied.)
  const QV_FINISH_COPY = {
    'Ultra Flat Matt': 'The ultimate paint for your whole home in an ultra flat matt finish. Our highest-performance paint is made uniquely for multiple rooms and surfaces from high-traffic areas, kitchens and bathrooms to woodwork and radiators making it perfect for colour drenching.',
    'Matt': 'Our high-quality, low-sheen emulsion with excellent coverage and hiding power recommended for use across your walls and ceilings in lower-traffic areas like bedrooms and living rooms.',
    'Eggshell': 'Our high-quality, low-sheen emulsion with excellent coverage and hiding power recommended for use across your walls and ceilings in lower-traffic areas like bedrooms and living rooms.',
    'Sample': 'Our mess-free Peel & Stick samples are the best way to start your decorating journey. Simply peel and stick them to your walls, ceiling or woodwork to see how the colour will look in your room. Move them around your room and leave them up for 24 hours to see how the colour changes in different lights.',
  };
  const QV_RECOMMENDED = 'Ultra Flat Matt';
  const QV_FINISH_ORDER = ['Ultra Flat Matt', 'Matt', 'Eggshell'];

  // Shows one variant: the form's id, price, image and button. A variant with no image of
  // its own shows the product's, not the last finish's tin.
  function qvApply(dialog, v) {
    dialog.querySelector('[data-qv-id]').value = v.id;
    dialog.querySelector('[data-qv-price]').textContent = qvMoney(Number(v.price));
    const img = dialog.querySelector('[data-qv-image]');
    const src = v.image || dialog.dataset.productImage;
    if (src) img.src = qvImage(src, 900);
    const atc = dialog.querySelector('.quick-view__atc');
    atc.disabled = !v.available;
    atc.textContent = v.available ? 'Add to basket' : 'Sold out';
    atc.form.dataset.qvAvailable = String(!!v.available);
  }

  // The add-to-cart handler's 2s reset re-enables its button; a sold-out variant picked in
  // the meantime must stay "Sold out"
  function qvKeepSoldOut(form, btn) {
    if (form.dataset.qvAvailable !== 'false') return;
    btn.disabled = true;
    btn.textContent = 'Sold out';
  }

  // Builds the finish tiles for a paint with a Finish option; false when it has none
  // (it then keeps the dropdown). Built with DOM methods so product text is never HTML.
  function qvPaintTiles(dialog, product) {
    const names = (product.options || []).map((o) => (typeof o === 'string' ? o : o.name));
    const fi = names.indexOf('Finish');
    if (fi < 0) return false;
    const si = names.indexOf('Size');
    const opt = (v, i) => (i >= 0 ? v['option' + (i + 1)] : null);
    const items = product.variants.map((v) => {
      const sample = opt(v, si) === 'Sample';
      return { v, sample, finish: sample ? 'Sample' : opt(v, fi) };
    });
    // Besides the finish, show the size, plus any other option the full-size tins differ by
    const full = items.filter((it) => !it.sample);
    const varies = names.map((_, i) => i !== fi && (i === si || new Set(full.map((it) => opt(it.v, i))).size > 1));
    const rank = (it) => (it.sample ? 99 : (QV_FINISH_ORDER.indexOf(it.finish) + 1 || 50));
    items.sort((a, b) => rank(a) - rank(b));

    const tilesEl = dialog.querySelector('[data-qv-tiles]');
    const copyEl = dialog.querySelector('[data-qv-copy]');
    tilesEl.replaceChildren();
    const recommended = items.find((it) => !it.sample && it.finish === QV_RECOMMENDED);
    const show = (it) => {
      qvApply(dialog, {
        id: it.v.id, price: it.v.price, available: it.v.available,
        image: it.v.featured_image && it.v.featured_image.src,
      });
      const copy = QV_FINISH_COPY[it.finish] || '';
      copyEl.textContent = copy;
      copyEl.hidden = !copy;
    };
    items.forEach((it) => {
      const tile = document.createElement('label');
      tile.className = 'quick-view__tile';
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'qv-finish';
      input.value = String(it.v.id);
      input.className = 'quick-view__tile-input';
      // each radio points at the shared description, so moving between finishes reads it
      input.setAttribute('aria-describedby', 'QuickViewFinishCopy');
      input.disabled = !it.v.available;
      input.addEventListener('change', () => { if (input.checked) show(it); });
      tile.append(input);
      if (it === recommended && it.v.available) {
        tile.classList.add('is-recommended');
        const badge = document.createElement('span');
        badge.className = 'quick-view__badge';
        badge.textContent = 'Recommended';
        tile.append(badge);
      }
      const name = document.createElement('span');
      name.className = 'quick-view__tile-name';
      name.textContent = it.finish;
      const detail = document.createElement('span');
      detail.className = 'quick-view__tile-detail';
      detail.textContent = it.sample ? '' : names.map((_, i) => (varies[i] ? opt(it.v, i) : null)).filter(Boolean).join(' · ');
      const price = document.createElement('span');
      price.className = 'quick-view__tile-price';
      price.textContent = it.v.available ? qvMoney(it.v.price) : 'Sold out';
      tile.append(name, detail, price);
      if (!it.v.available) tile.classList.add('is-sold-out');
      tilesEl.append(tile);
    });
    // Ultra Flat Matt first if it can be bought, then the first that can, then the first
    const start = (recommended && recommended.v.available ? recommended : null)
      || items.find((it) => it.v.available) || items[0];
    const startInput = tilesEl.querySelector(`input[value="${start.v.id}"]`);
    startInput.checked = true;
    show(start);
    return true;
  }

  function qvEnsureDialog() {
    let dialog = document.getElementById('QuickView');
    if (dialog) return dialog;
    dialog = document.createElement('dialog');
    dialog.id = 'QuickView';
    dialog.className = 'quick-view';
    dialog.innerHTML = `
      <button type="button" class="quick-view__close" data-qv-close aria-label="Close quick view">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M2 2L14 14M14 2L2 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
      </button>
      <div class="quick-view__media"><img alt="" data-qv-image></div>
      <div class="quick-view__body">
        <p class="quick-view__title t-product-name" data-qv-title></p>
        <p class="quick-view__price" data-qv-price></p>
        <form method="post" action="/cart/add" data-add-to-cart class="quick-view__form">
          <input type="hidden" name="id" data-qv-id>
          <label class="visually-hidden" for="QuickViewVariant">Choose a variant</label>
          <select id="QuickViewVariant" class="quick-view__variants" data-qv-variants></select>
          <fieldset class="quick-view__finishes" data-qv-finishes hidden>
            <legend class="visually-hidden">Choose a finish</legend>
            <div class="quick-view__tiles" data-qv-tiles></div>
          </fieldset>
          <p class="quick-view__finish-copy" id="QuickViewFinishCopy" data-qv-copy hidden></p>
          <button type="submit" class="btn btn--dark quick-view__atc">Add to basket</button>
        </form>
        <a href="#" class="cm-link quick-view__link" data-qv-link>View full details</a>
      </div>`;
    document.body.appendChild(dialog);
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog || e.target.closest('[data-qv-close]')) dialog.close();
    });
    dialog.querySelector('[data-qv-variants]').addEventListener('change', (e) => {
      const opt = e.target.selectedOptions[0];
      qvApply(dialog, {
        id: opt.value, price: opt.dataset.price,
        available: opt.dataset.available === 'true', image: opt.dataset.image,
      });
    });
    return dialog;
  }

  document.addEventListener('click', async (e) => {
    const trigger = e.target.closest('[data-quick-view]');
    if (!trigger) return;
    e.preventDefault();

    const dialog = qvEnsureDialog();
    dialog.classList.add('is-loading');
    dialog.showModal();

    try {
      const product = await fetch(`/products/${trigger.dataset.quickView}.js`)
        .then((r) => { if (!r.ok) throw new Error('not found'); return r.json(); });

      dialog.querySelector('[data-qv-title]').textContent = product.title;
      dialog.querySelector('[data-qv-image]').src = qvImage(product.featured_image, 900);
      dialog.querySelector('[data-qv-image]').alt = product.title;
      dialog.querySelector('[data-qv-link]').href = trigger.dataset.productUrl || `/products/${product.handle}`;

      const select = dialog.querySelector('[data-qv-variants]');
      const finishes = dialog.querySelector('[data-qv-finishes]');
      const copyEl = dialog.querySelector('[data-qv-copy]');
      // Paint with a Finish option gets the tiles; everything else keeps the dropdown
      dialog.dataset.productImage = product.featured_image || '';
      const tiled = String(product.type || '').toLowerCase() === 'paint' && qvPaintTiles(dialog, product);
      finishes.hidden = !tiled;
      if (tiled) { select.style.display = 'none'; return; }
      // no stale finish radios from an earlier paint left in the form
      dialog.querySelector('[data-qv-tiles]').replaceChildren();
      copyEl.hidden = true;
      select.innerHTML = product.variants.map((v) =>
        `<option value="${v.id}" data-price="${v.price}" data-available="${v.available}"
           data-image="${(v.featured_image && v.featured_image.src) || ''}">
           ${v.title} — ${qvMoney(v.price)}${v.available ? '' : ' (sold out)'}
         </option>`).join('');
      select.style.display = product.variants.length > 1 ? '' : 'none';

      const first = product.variants.find((v) => v.available) || product.variants[0];
      select.value = String(first.id);
      select.dispatchEvent(new Event('change'));
    } catch (err) {
      console.error('Quick view error:', err);
      dialog.close();
      const url = trigger.dataset.productUrl;
      if (url) window.location.href = url;
    } finally {
      dialog.classList.remove('is-loading');
    }
  });

  // ── INTERSECTION OBSERVER (scroll reveal) ─────────────────
  if ('IntersectionObserver' in window) {
    const revealEls = document.querySelectorAll('.product-card, .room-card, .sample-cta__content, .sample-cta__media');

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

    revealEls.forEach((el, i) => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(24px)';
      el.style.transition = `opacity 0.55s ease ${i * 0.05}s, transform 0.55s ease ${i * 0.05}s`;
      observer.observe(el);
    });
  }

})();
